import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {clone} from "lodash";
import {Entity} from "./entity";
import {toTransportCategory, TransportCategory} from "./transport-category";
import {CargoSubtype, toCargoSubtype} from "./cargo-subtype";

export const AllocatableLoadValidator = t.intersection([
  t.type({
    id: t.string,
    containerId: t.string,
    regionId: t.string,
    volume: t.string,
    volumeUnitOfMeasure: t.string,
    cargoSubtype: t.string,
    category: t.string,
    dttm: t.number,
  }),
  t.partial({
    containerVolumeDropStartDttm: t.number,
    containerVolumeDropEndDttm: t.number,
    containerVolumeDropVolume: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    // extras
    containerName: t.string,
    siteName: t.string,
  }),
]);

export const AllocatableLoadArrayValidator = t.array(AllocatableLoadValidator);

export type AllocatableLoad = t.TypeOf<typeof AllocatableLoadValidator>;
export type AllocatableLoadArray = t.TypeOf<typeof AllocatableLoadArrayValidator>;

@Exclude()
export class AllocatableLoadEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  containerId!: string;

  @Expose()
  regionId!: string;

  @Expose()
  volume!: number

  @Expose()
  volumeUnitOfMeasure!: string

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCargoSubtype))
  cargoSubtype!: CargoSubtype;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory;

  @Expose()
  dttm!: number;

  @Expose()
  containerVolumeDropStartDttm?: number;

  @Expose()
  containerVolumeDropEndDttm?: number;

  @Expose()
  containerVolumeDropVolume?: number;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  containerName?: string

  @Expose()
  siteName?: string

  readonly entityType = 'ALLOCATABLE_LOAD';

  get displayName(): Readonly<string> {
    return this.id
  }

  /**
   * Constructor for AllocatableLoadEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(plain: AllocatableLoad): AllocatableLoadEntity {
    return plainToInstance(AllocatableLoadEntity, plain, {
      excludeExtraneousValues: true,
    });
  }

  loadPlainValue(): Promise<AllocatableLoad> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(AllocatableLoadValidator, object);
  }

  asPlainValue(): AllocatableLoad {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(AllocatableLoadValidator, object);
  }

  clone(): AllocatableLoadEntity {
    return clone(this);
  }

  get resolved(): boolean {
    return typeof this.containerVolumeDropStartDttm !== 'undefined' && typeof this.containerVolumeDropEndDttm !== 'undefined'
  }
}
